import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useEnvs } from '@next-box/envs';
import { type ScreenType } from '@tmdb-graphql-api/schema/types';
import { useEffect, useState } from 'react';
import { FeaturePlayer } from '#components/FeaturePlayer/index.tsx';
import { VideoModal } from '#components/Modal/VideoModal.tsx';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { CollectionDrawer } from './CollectionDrawer.tsx';
import { formatCollection } from './helpers/formatCollection.ts';
import { getNextMovie, getPrevMovie } from './helpers/getNextPrevMovie.ts';
import { type MovieCollectionData } from './types.ts';

export type FeaturePlayerMovieModalProps = {
  id: string | number;
  onClose: () => void;
  open: boolean;
  title: string;
  type: ScreenType;
};

const GET_MOVIE_COLLECTION = "\nquery GetMovieCollection(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieCollection\n  }\n}\n\nfragment MovieCollection on Movie {\n  belongsToCollection {\n    name\n    parts {\n      backdropPath\n      posterPath\n      releaseDate\n      runtime\n      status\n      tagline\n      title\n      voteAverage\n      voteCount\n    }\n  }\n}\n";

export const FeaturePlayerMovieModal = ({ id, title, type, ...rest }: FeaturePlayerMovieModalProps) => {
  const { getEnv } = useEnvs();
  const { config } = useToolbelt();
  const addRecentlyWatchedMovie = useStore(store => store.addRecentlyWatchedMovie);
  const [collectionDrawOpen, setCollectionDrawOpen] = useState(false);
  const [partId, setPartId] = useState<string | number>(id);
  const baseUrl = getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_PROVIDER') ?? '';

  const { execute, loading, result } = useQuery<MovieCollectionData>(GET_MOVIE_COLLECTION, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'FeaturePlayerMovieModal' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const { name } = result?.data.movie.belongsToCollection ?? {};
  const items = formatCollection(result?.data, config);
  const hasCollection = !loading && items.length > 0;
  const currentMovie = items.find(item => item.id === partId);
  const nextMovieId = getNextMovie(items, partId);
  const prevMovieId = getPrevMovie(items, partId);

  return (
    <>
      <VideoModal
        {...rest}
        hasNext={!!nextMovieId}
        hasPrev={!!prevMovieId}
        id={id}
        onNext={() => {
          if (nextMovieId) {
            setPartId(nextMovieId);
          }
        }}
        onOpenPlaylist={() => {
          setCollectionDrawOpen(true);
        }}
        onPrev={() => {
          if (prevMovieId) {
            setPartId(prevMovieId);
          }
        }}
        playlist={hasCollection}
        title={currentMovie?.title ?? title}
      >
        <FeaturePlayer
          onClick={() => {
            // @ts-expect-error TODO: Need to refactor so partId is string
            addRecentlyWatchedMovie(partId);
          }}
          url={`${atob(baseUrl)}/${type.toLowerCase()}/${String(partId)}`}
        />
      </VideoModal>
      {hasCollection ? (
        <CollectionDrawer
          items={items}
          name={name}
          onPartClick={part => {
            setPartId(part);
            setCollectionDrawOpen(false);
          }}
          open={collectionDrawOpen}
          selectedPartId={partId}
          setOpen={setCollectionDrawOpen}
        />
      ) : undefined}
    </>
  );
};
